import { createEvent } from "library/local-events/index.mjs"

/**
 * Event-based registry of the field TYPES a share-link creator can request from recipients.
 *
 * Each type self-registers exactly one entry (and its renderers) from its own plugin runtime in
 * `field-types/<type>.runtime.js`, so adding a new field type is a drop-in: one client plugin file,
 * zero edits to the core host components or the server. This replaces the previous hardcoded
 * `REQUESTED_FIELD_TYPES` array.
 *
 * Collect-a-list pattern (see CLAUDE.md / INVERSION-OF-CONTROL.md): contributors push onto the
 * mutable `types` array via `handleOnce`, and `getRequestedFieldTypes()` raises the event with a
 * fresh array and reads back what every contributor pushed.
 *
 * Feature-local; `.js` is fine because the registry is client-only (the server is type-permissive
 * and does not consult this list).
 */
export const RegisterRequestedFieldType = createEvent("RegisterRequestedFieldType")

const DEFAULT_PRIORITY = 100
const FALLBACK_DEFAULT_TYPE = "text"

/**
 * The registered requested-field types, ordered by ascending `priority` (default 100) and de-duped by
 * `value` (first registration for a value wins). Each entry is `{ value, label, priority }`.
 *
 * @returns {Array<{value: string, label: string, priority: number}>}
 */
export function getRequestedFieldTypes() {
    const { types } = RegisterRequestedFieldType.call({ types: [] })
    return dedupeByValue(types).sort(byPriority)
}

/**
 * The `value` of the first (lowest-priority) registered type, used as the default for a newly added
 * field. Falls back to "text" when nothing is registered (e.g. in isolated unit tests).
 *
 * @returns {string}
 */
export function getDefaultRequestedFieldType() {
    return getRequestedFieldTypes()[0]?.value ?? FALLBACK_DEFAULT_TYPE
}

/**
 * Whether `type` is a registered requested-field type that has a dedicated `RequestedFieldInput` slot
 * plug. The input/value hosts use this to decide whether to render the generic fallback control (for
 * unknown/legacy types) instead of relying on the slot.
 *
 * @param {string} type
 * @returns {boolean}
 */
export function isKnownRequestedFieldType(type) {
    return getRequestedFieldTypes().some((registered) => registered.value === type)
}

function dedupeByValue(types) {
    const seen = new Set()
    const result = []
    for (const type of types ?? []) {
        if (!type || typeof type.value !== "string") continue
        if (seen.has(type.value)) continue
        seen.add(type.value)
        result.push({ ...type, priority: type.priority ?? DEFAULT_PRIORITY })
    }
    return result
}

function byPriority(left, right) {
    return left.priority - right.priority
}
